import React, { useEffect, useRef, useState } from "react";
import { Share2, Link as LinkIcon, Facebook } from "lucide-react";
import { toast } from "sonner";
import { track } from "../lib/analytics";
import { absolutePlaceUrl } from "../lib/placeUrl";

/**
 * Share-this-place button for /park/{id} detail pages.
 *
 * Behavior:
 *   - Mobile / supporting browsers: native navigator.share() (iMessage,
 *     WhatsApp, IG Stories, email, …) — one tap, no UI of ours.
 *   - Desktop / unsupported: small popover with "Copy Link" + "Share
 *     on Facebook". Copy uses navigator.clipboard.writeText with a
 *     document.execCommand fallback so it works on non-HTTPS dev
 *     and older browsers.
 *
 * GA4 event `place_shared` fires on every successful share with
 * `share_method ∈ {native, copy_link, facebook}`. The cancelled-share
 * branch of navigator.share rejects with `AbortError` — we swallow
 * that silently so dropping the picker doesn't fire a misleading event.
 */

// Canonical URL we share for any place. Hard-coded to pawground.app
// so the share works regardless of which environment the user is on
// (preview, prod, etc.) — shares should always point to the live URL.
const SHARE_BASE = "https://pawground.app";

const placeUrlFor = (place) => {
  // All URL routing flows through the shared placeUrl() helper.
  // absolutePlaceUrl prepends the canonical share host so shared links
  // always point to production regardless of which environment
  // generated the URL.
  return absolutePlaceUrl(place, SHARE_BASE);
};

// Web Share API is gated to secure contexts (https) and only present
// on some desktop browsers. We check `canShare` defensively because a
// few Android browsers expose `share` but reject our payload.
const supportsNativeShare = () => {
  if (typeof navigator === "undefined") return false;
  if (typeof navigator.share !== "function") return false;
  // canShare is optional — when present we use it as the gate
  if (typeof navigator.canShare === "function") {
    try {
      return navigator.canShare({ url: SHARE_BASE });
    } catch {
      return false;
    }
  }
  return true;
};

const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to execCommand
    }
  }
  // Legacy fallback for non-secure contexts or denied clipboard perms.
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "absolute";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

const ShareButton = ({ place }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close popover on click-outside + Escape so it behaves like a
  // proper modal-lite. The ref guard avoids closing when we click
  // INSIDE the popover (e.g., on the buttons).
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!place?.id) return null;

  const url = placeUrlFor(place);
  const name = place.name || "this dog-friendly place";
  const category = place.place_type || "place";

  const fireTracking = (method) => {
    track("place_shared", {
      place_id: place.id,
      place_name: name,
      place_category: category,
      share_method: method,
    });
  };

  const handleClick = async () => {
    if (supportsNativeShare()) {
      try {
        await navigator.share({
          title: name,
          text: `Check out ${name} on Pawground! 🐾`,
          url,
        });
        fireTracking("native");
      } catch (err) {
        // User cancelled = AbortError. NotAllowedError = perms.
        // In either case, fall back to the popover so they can
        // still copy/Facebook from desktop.
        if (err?.name === "AbortError") return;
        setOpen(true);
      }
      return;
    }
    setOpen((v) => !v);
  };

  const handleCopy = async () => {
    const ok = await copyToClipboard(url);
    setOpen(false);
    if (ok) {
      fireTracking("copy_link");
      // Sonner toast positioned bottom-center per spec. Custom inline
      // styles match the ink-black-on-white pill the spec asks for.
      toast("Link copied!", {
        duration: 2000,
        position: "bottom-center",
        unstyled: true,
        classNames: {
          toast:
            "rounded-full px-5 py-2.5 text-sm font-body font-semibold shadow-warm-lg",
        },
        style: {
          backgroundColor: "#1A1A0F",
          color: "#FFFFFF",
        },
      });
    } else {
      toast.error("Couldn't copy. You can copy the URL from your browser bar.");
    }
  };

  const handleFacebook = () => {
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(shareUrl, "_blank", "noopener,noreferrer");
    fireTracking("facebook");
    setOpen(false);
  };

  return (
    <div className="relative inline-block" ref={wrapperRef}>
      <button
        type="button"
        onClick={handleClick}
        data-testid="share-btn"
        aria-label="Share this place"
        aria-haspopup="menu"
        aria-expanded={open}
        className="font-body font-semibold rounded-full px-3 sm:px-5 py-3.5 transition-all active:scale-95 flex items-center gap-2 border-2"
        style={{
          backgroundColor: "#FFFFFF",
          borderColor: "#D4C9B0",
          color: "#1A1A0F",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#E8F2EC";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "#FFFFFF";
        }}
      >
        <Share2 className="w-4 h-4" />
        <span className="hidden sm:inline">Share</span>
      </button>

      {open && (
        <div
          role="menu"
          data-testid="share-popover"
          className="absolute right-0 z-40 mt-2 w-56 rounded-2xl border p-1.5 shadow-warm-lg"
          style={{
            backgroundColor: "#FFFFFF",
            borderColor: "#D4C9B0",
          }}
        >
          <button
            type="button"
            role="menuitem"
            onClick={handleCopy}
            data-testid="share-copy-link"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-body font-medium text-left transition-colors hover:bg-[#F5F0E8]"
            style={{ color: "#1A1A0F" }}
          >
            <LinkIcon className="w-4 h-4" />
            Copy Link
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={handleFacebook}
            data-testid="share-facebook"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-body font-medium text-left transition-colors hover:bg-[#F5F0E8]"
            style={{ color: "#1A1A0F" }}
          >
            <Facebook className="w-4 h-4" />
            Share on Facebook
          </button>
        </div>
      )}
    </div>
  );
};

export default ShareButton;
